// src/components/Footer.js - BULLETPROOF Footer Component
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Memoized click handlers to prevent re-renders
  const handleSocialClick = useCallback((platform) => {
    const socialUrls = {
      instagram: 'https://instagram.com/tiwatinwa',
      facebook: 'https://facebook.com/tiwatinwa',
      twitter: 'https://twitter.com/tiwatinwa',
      whatsapp: 'https://wa.me/2348012345678'
    };
    
    try {
      if (socialUrls[platform]) {
        window.open(socialUrls[platform], '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.log('Unable to open social link:', error);
    }
  }, []);

  const handleStoreClick = useCallback((store) => {
    const storeUrls = {
      appstore: 'https://apps.apple.com/app/tiwatinwa',
      playstore: 'https://play.google.com/store/apps/details?id=com.tiwatinwa'
    };
    
    try {
      if (storeUrls[store]) {
        window.open(storeUrls[store], '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.log('Unable to open store link:', error);
    }
  }, []);

  const handlePolicyClick = useCallback((type) => {
    alert(`${type} - Coming Soon! 🚀`);
  }, []);

  // Inline styles with fallback colors
  const footerStyles = {
    footer: {
      background: 'linear-gradient(135deg, #008751, #fb923c)',
      color: 'white',
      padding: '3rem 0 2rem',
      marginTop: 'auto'
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '3rem',
      marginBottom: '2rem'
    },
    brandIcon: {
      width: '50px',
      height: '50px',
      background: 'rgba(255,255,255,0.2)',
      borderRadius: '15px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.8rem',
      backdropFilter: 'blur(10px)'
    },
    socialButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '50px',
      height: '50px',
      background: 'rgba(255,255,255,0.15)',
      borderRadius: '15px',
      fontSize: '1.5rem',
      border: '2px solid rgba(255,255,255,0.2)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(5px)',
      color: 'white'
    },
    storeButton: {
      background: 'rgba(255,255,255,0.2)',
      border: '2px solid rgba(255,255,255,0.3)',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    policyButton: {
      background: 'none',
      border: 'none',
      color: 'rgba(255,255,255,0.8)',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      fontSize: '0.9rem',
      fontFamily: 'inherit'
    }
  };

  return (
    <footer style={footerStyles.footer}>
      <div style={footerStyles.container}>
        <div style={footerStyles.grid}>
          {/* Brand Section */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={footerStyles.brandIcon}>
                🍽️
              </div>
              <div>
                <h3 style={{
                  fontSize: '1.8rem',
                  fontWeight: '900',
                  margin: 0,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                }}>
                  TiwaTinwa
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  margin: 0,
                  opacity: 0.9
                }}>
                  Authentic Nigerian Cuisine
                </p>
              </div>
            </div>
            <p style={{
              fontSize: '1rem',
              lineHeight: '1.6',
              opacity: 0.9,
              margin: 0
            }}>
              Bringing the authentic taste of Nigeria to your doorstep. 
              Fresh ingredients, traditional recipes, and modern convenience.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              marginBottom: '1.5rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}>
              🔗 Quick Links
            </h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.8rem'
            }}>
              {[
                { to: '/', label: '🏠 Home' },
                { to: '/menu', label: '🍽️ Menu' },
                { to: '/contact', label: '📞 Contact Us' }
              ].map((link, index) => (
                <Link
                  key={index}
                  to={link.to}
                  style={{
                    color: 'rgba(255,255,255,0.9)',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    padding: '0.5rem 0',
                    display: 'block'
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              marginBottom: '1.5rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}>
              📞 Get in Touch
            </h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <span style={{ fontSize: '1.2rem' }}>📞</span>
                <a
                  href="tel:+2348012345678"
                  style={{
                    color: 'rgba(255,255,255,0.9)',
                    textDecoration: 'none',
                    fontSize: '1rem'
                  }}
                >
                  +234 801 234 5678
                </a>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <span style={{ fontSize: '1.2rem' }}>📧</span>
                <a
                  href="mailto:hello@tiwatinwa.com"
                  style={{
                    color: 'rgba(255,255,255,0.9)',
                    textDecoration: 'none',
                    fontSize: '1rem'
                  }}
                >
                  hello@tiwatinwa.com
                </a>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <span style={{ fontSize: '1.2rem' }}>📍</span>
                <span style={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '1rem'
                }}>
                  Lagos, Abuja, Port Harcourt
                </span>
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div>
            <h4 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              marginBottom: '1.5rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}>
              🌟 Follow Us
            </h4>
            <div style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap'
            }}>
              {[
                { platform: 'instagram', icon: '📸', color: '#e4405f' },
                { platform: 'facebook', icon: '📘', color: '#1877f2' },
                { platform: 'twitter', icon: '🐦', color: '#1da1f2' },
                { platform: 'whatsapp', icon: '💬', color: '#25d366' }
              ].map((social, index) => (
                <button
                  key={index}
                  onClick={() => handleSocialClick(social.platform)}
                  style={footerStyles.socialButton}
                  title={`Follow us on ${social.platform}`}
                  aria-label={`Follow us on ${social.platform}`}
                >
                  {social.icon}
                </button>
              ))}
            </div>

            {/* App Download */}
            <div style={{ marginTop: '2rem' }}>
              <p style={{
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: '1rem',
                opacity: 0.9
              }}>
                📱 Download Our App
              </p>
              <div style={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <button 
                  onClick={() => handleStoreClick('appstore')}
                  style={footerStyles.storeButton}
                  title="Download from App Store"
                  aria-label="Download from App Store"
                >
                  🍎 App Store
                </button>
                <button 
                  onClick={() => handleStoreClick('playstore')}
                  style={footerStyles.storeButton}
                  title="Download from Google Play"
                  aria-label="Download from Google Play"
                >
                  🤖 Google Play
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={{
          borderTop: '2px solid rgba(255,255,255,0.2)',
          paddingTop: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{
            fontSize: '0.9rem',
            opacity: 0.9
          }}>
            © {currentYear} TiwaTinwa. All rights reserved. Made with ❤️ in Nigeria 🇳🇬
          </div>
          
          <div style={{
            display: 'flex',
            gap: '2rem',
            fontSize: '0.9rem'
          }}>
            <button
              onClick={() => handlePolicyClick('Privacy Policy')}
              style={footerStyles.policyButton}
              title="View Privacy Policy"
              aria-label="View Privacy Policy"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => handlePolicyClick('Terms of Service')}
              style={footerStyles.policyButton}
              title="View Terms of Service"
              aria-label="View Terms of Service"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;